import { useQuery } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi, pedirBinario } from "@/api/cliente"
import { resumirSenal } from "@/componentes/soporte/guion"

/**
 * Un reporte entero, con su evidencia, y el triaje.
 *
 * ⚠ **El motivo del veredicto se enseña arriba del todo.** Es lo que permite a dev
 * discutir la clasificación en vez de aceptarla: dice qué evidencia concreta la
 * produjo, y si esa evidencia no era lo que el clasificador creyó, se ve enseguida.
 *
 * ⚠ **Descartar no borra la tarea: la cancela.** Cancelada queda en el tablero con
 * su motivo, que es lo que permite ver después cuántos reportes de una pantalla
 * acabaron en nada — la señal de que esa pantalla confunde, aunque no falle.
 */

type Detalle = {
  id: number
  vista: string
  vista_al_reportar: string
  titulo: string
  que_intentaba: string
  que_paso: string
  senales: Record<string, never>[] | null
  migas: { momento: string; tipo: string; etiqueta: string }[] | null
  navegador: string | null
  veredicto: "FALLA" | "RECHAZO_ESPERADO" | "INDETERMINADO"
  confianza: string
  motivo: string
  tiene_captura: boolean
  estado: "NUEVO" | "ACEPTADO" | "DESCARTADO"
  tarea_id: number | null
  resolucion: string | null
}

const NOMBRE_MIGA: Record<string, string> = {
  CLIC: "Pulsó",
  RUTA: "Fue a",
  ENVIO: "Envió",
  ESCRITURA: "Escribió en",
}

export function DetalleReporte({
  reporteId,
  triando,
  error,
  onTriar,
}: {
  reporteId: number
  triando: boolean
  error: unknown
  onTriar: (acepta: boolean, resolucion: string) => void
}) {
  const [resolucion, setResolucion] = useState("")

  const reporte = useQuery({
    queryKey: ["reporte", reporteId],
    queryFn: () => api.get<Detalle>(`/api/v1/reportes/${reporteId}`),
  })

  const captura = useQuery({
    queryKey: ["reporte-captura", reporteId],
    queryFn: async () =>
      URL.createObjectURL(await pedirBinario(`/api/v1/reportes/${reporteId}/captura`)),
    enabled: reporte.data?.tiene_captura === true,
    gcTime: 0,
  })

  if (reporte.isPending) {
    return (
      <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (reporte.isError || reporte.data === undefined) {
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudo cargar el reporte.
      </p>
    )
  }

  const r = reporte.data
  const cerrado = r.estado !== "NUEVO"

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">{r.titulo}</h3>
        <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {r.vista}
          {r.vista !== r.vista_al_reportar && ` · reportado desde ${r.vista_al_reportar}`}
        </p>
      </div>

      {/* ⚠ El veredicto NUNCA sin su motivo. */}
      <div className="superficie p-3">
        <p className="text-xs font-medium uppercase" style={{ color: "var(--fg-muted)" }}>
          Veredicto automático · {r.veredicto} · confianza {r.confianza}
        </p>
        <p className="mt-1 text-sm">{r.motivo}</p>
      </div>

      <Campo titulo="Qué intentaba" valor={r.que_intentaba} />
      <Campo titulo="Qué pasó" valor={r.que_paso} />

      {r.senales !== null && r.senales.length > 0 && (
        <section>
          <h4 className="rotulo mb-2">Errores registrados</h4>
          <ul className="ml-4 list-disc text-sm">
            {r.senales.map((s, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: una señal no tiene id y su posición ES su identidad
              <li key={`s-${i}`}>{resumirSenal(s as never)}</li>
            ))}
          </ul>
        </section>
      )}

      {r.migas !== null && r.migas.length > 0 && (
        <section>
          <h4 className="rotulo mb-2">Qué hizo antes</h4>
          <ol className="ml-4 list-decimal text-sm">
            {r.migas.map((m, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: una miga no tiene id y su posición ES su identidad
              <li key={`m-${i}`}>
                {NOMBRE_MIGA[m.tipo] ?? m.tipo} «{m.etiqueta}»
              </li>
            ))}
          </ol>
        </section>
      )}

      {captura.data !== undefined && (
        <section>
          <h4 className="rotulo mb-2">Captura</h4>
          <img
            src={captura.data}
            alt={`Captura del reporte ${r.id}`}
            className="w-full rounded-[var(--radius-tarjeta)]"
            style={{ border: "1px solid var(--border)" }}
          />
        </section>
      )}

      {r.navegador !== null && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {r.navegador}
        </p>
      )}

      {cerrado ? (
        <div className="superficie p-3 text-sm">
          <strong>{r.estado}</strong>
          {r.resolucion !== null && `: ${r.resolucion}`}
        </div>
      ) : (
        <section>
          <h4 className="rotulo mb-2">Triaje</h4>
          {/* ⚠ Obligatorio en los dos sentidos: aceptar sin decir qué se va a hacer,
              o descartar sin decir por qué, deja una fila que dice que alguien lo
              miró y nada más. */}
          <label htmlFor="resolucion" className="mb-1 block text-xs font-medium uppercase">
            Qué se hace con esto
          </label>
          <textarea
            id="resolucion"
            rows={2}
            minLength={5}
            className="campo"
            value={resolucion}
            onChange={(e) => setResolucion(e.target.value)}
            placeholder="Reproducido: el PATCH revienta cuando la tarea no tiene dueño."
          />

          {error !== null && error !== undefined && (
            <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
              {error instanceof ErrorApi ? error.message : "No se pudo triar"}
            </p>
          )}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={resolucion.trim().length < 5 || triando}
              onClick={() => onTriar(true, resolucion)}
              data-testid="aceptar-reporte"
              className="boton-primario h-9 px-3 text-sm"
            >
              Aceptar: hay trabajo
            </button>
            <button
              type="button"
              disabled={resolucion.trim().length < 5 || triando}
              onClick={() => onTriar(false, resolucion)}
              data-testid="descartar-reporte"
              className="boton-secundario h-9 px-3 text-sm"
            >
              Descartar y cancelar la tarea
            </button>
          </div>
        </section>
      )}
    </div>
  )
}

function Campo({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase" style={{ color: "var(--fg-muted)" }}>
        {titulo}
      </p>
      <p className="text-sm">{valor}</p>
    </div>
  )
}
